import { useEffect, useState } from "react";
import { Copy, Check, Clock, Hash, ArrowRight } from "lucide-react";
import { PaymentCategory } from "@/features/applications/repositories/licences-repository.ts";
import Button from "@/components/buttons/Button";
import {
    generateControlNumber,
    normalizeTzPhone,
    isValidTzPhone,
    type MobileProvider,
    type ZanCustomerFields,
    type ZanMalipoResult,
} from "@/features/applications/services/applications-services";

interface IProps extends ZanCustomerFields {
    onSubmit?: () => void;
    paymentCategory: PaymentCategory;
    onPaymentSuccess?: () => void;
    totalFees?: string;
    defaultPhoneNumber?: string;
}

type Mode = 'choose' | 'collect' | 'generating' | 'ready';

const PROVIDERS: { value: MobileProvider; label: string }[] = [
    { value: 'MPESA', label: 'M-Pesa (Vodacom)' },
    { value: 'TIGOPESA', label: 'Tigo Pesa' },
    { value: 'AIRTEL_MONEY', label: 'Airtel Money' },
    { value: 'HALOPESA', label: 'Halo Pesa' },
    { value: 'EAZYPESA', label: 'Eazy Pesa (Zantel)' },
    { value: 'TPESA', label: 'T-Pesa (TTCL)' },
];

function detectProvider(phone: string): MobileProvider | '' {
    const digits = phone.replace(/\D/g, '');
    const suffix = digits.length === 12 ? digits.slice(3) : digits.length === 10 ? digits.slice(1) : null;
    if (!suffix) return '';
    const p3 = suffix.slice(0, 3);
    if (['074', '075', '076', '077', '078'].includes(p3)) return 'MPESA';
    if (['065', '067', '071'].includes(p3)) return 'TIGOPESA';
    if (['068', '069'].includes(p3)) return 'AIRTEL_MONEY';
    if (p3 === '062') return 'HALOPESA';
    if (p3 === '079') return 'EAZYPESA';
    if (p3 === '041') return 'TPESA';
    return '';
}

export default function ControlNumberPaymentForm({
    onSubmit,
    paymentCategory: _paymentCategory,
    onPaymentSuccess: _onPaymentSuccess,
    totalFees,
    defaultPhoneNumber,
    ...customerFields
}: IProps) {
    const [mode, setMode] = useState<Mode>('choose');
    const [copied, setCopied] = useState(false);
    const [phone, setPhone] = useState('');
    const [provider, setProvider] = useState<MobileProvider | ''>('');
    const [controlNumber, setControlNumber] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!defaultPhoneNumber) return;
        const normalized = normalizeTzPhone(defaultPhoneNumber);
        if (!normalized) return;
        const local = normalized.startsWith('255') ? normalized.slice(3) : normalized;
        setPhone(local);
        const detected = detectProvider(normalized);
        if (detected) setProvider(detected);
    }, [defaultPhoneNumber]);

    const handlePhoneChange = (val: string) => {
        const d = val.replace(/\D/g, '');
        setPhone(d);
        const detected = detectProvider(d);
        if (detected) setProvider(detected);
    };

    const handleGenerate = async () => {
        const normalized = normalizeTzPhone(phone);
        if (!isValidTzPhone(normalized)) {
            setError('Phone number must be 12 digits starting with country code, e.g. 255712345678.');
            return;
        }
        if (!provider) {
            setError('Please select your mobile provider.');
            return;
        }
        setError(null);
        setMode('generating');
        try {
            const res = await generateControlNumber({
                amount: Number(totalFees ?? '0'),
                phoneNumber: normalized,
                provider: provider as MobileProvider,
                ...customerFields,
            });
            const data: ZanMalipoResult = (res as any)?.results ?? res;
            setControlNumber(data?.controlNumber ?? null);
            setMode('ready');
        } catch (e: any) {
            setError(e?.response?.data?.message ?? e?.message ?? 'Failed to generate control number.');
            setMode('collect');
        }
    };

    const handleCopy = () => {
        if (!controlNumber) return;
        navigator.clipboard.writeText(controlNumber);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // — Choice screen —
    if (mode === 'choose') {
        return (
            <div className="space-y-3">
                <button
                    type="button"
                    onClick={() => setMode('collect')}
                    className="w-full flex items-start gap-4 p-4 rounded-2xl border-2 border-orange-200 bg-orange-50 hover:border-orange-400 transition-all text-left"
                >
                    <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Hash className="w-5 h-5 text-orange-500" />
                    </div>
                    <div>
                        <p className="font-semibold text-gray-900 text-sm">Generate Control Number</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Get a control number, then pay it at any bank, mobile app, or agent.
                        </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-orange-400 ml-auto self-center flex-shrink-0" />
                </button>

                <button
                    type="button"
                    onClick={() => onSubmit?.()}
                    className="w-full flex items-start gap-4 p-4 rounded-2xl border-2 border-gray-200 hover:border-gray-300 bg-white transition-all text-left"
                >
                    <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Clock className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                        <p className="font-semibold text-gray-900 text-sm">Pay Later</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Submit your application now. Payment status will be updated once received.
                        </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-300 ml-auto self-center flex-shrink-0" />
                </button>
            </div>
        );
    }

    // — Collect phone + provider —
    if (mode === 'collect') {
        return (
            <div className="space-y-4">
                <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Phone Number
                    </label>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">+255</span>
                        <input
                            type="tel"
                            value={phone}
                            onChange={e => handlePhoneChange(e.target.value)}
                            placeholder="7XXXXXXXX"
                            maxLength={12}
                            className="input input-default pl-14 w-full"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Network Provider
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                        {PROVIDERS.map(p => (
                            <button
                                key={p.value}
                                type="button"
                                onClick={() => setProvider(p.value)}
                                className={`px-3 py-2 rounded-xl border text-xs font-medium transition-all ${
                                    provider === p.value
                                        ? 'border-orange-400 bg-orange-50 text-orange-700'
                                        : 'border-gray-200 text-gray-500 hover:border-gray-300'
                                }`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-sm text-red-600">{error}</div>
                )}

                <Button
                    intent="primary"
                    size="md"
                    className="w-full"
                    disabled={!phone || !provider}
                    onClick={handleGenerate}
                    rightIcon={<ArrowRight className="w-4 h-4" />}
                >
                    Generate Control Number
                </Button>
            </div>
        );
    }

    // — Generating —
    if (mode === 'generating') {
        return (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
                <div className="w-10 h-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                <p className="text-sm text-gray-400">Generating your control number…</p>
            </div>
        );
    }

    // — Ready: show control number + submit —
    return (
        <div className="space-y-4">
            <div className="bg-gradient-to-b from-orange-50 to-white border border-orange-100 rounded-2xl p-6 text-center">
                <p className="text-[11px] font-semibold text-orange-400 uppercase tracking-[0.15em] mb-3">Control Number</p>
                <p className="text-4xl font-bold text-gray-900 tracking-[0.12em] font-mono">{controlNumber || '—'}</p>
                <button
                    onClick={handleCopy}
                    className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-orange-500 hover:text-orange-600 transition-colors"
                >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? 'Copied!' : 'Copy number'}
                </button>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                {[
                    'Open your mobile banking or mobile money app',
                    'Select "Pay Bill" or "Lipa"',
                    'Enter the control number above',
                    'Confirm the amount and complete payment',
                ].map((step, i) => (
                    <div key={i} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-orange-100 text-orange-500 text-[11px] font-bold flex items-center justify-center mt-0.5">
                            {i + 1}
                        </span>
                        <p className="text-sm text-gray-600">{step}</p>
                    </div>
                ))}
            </div>

            <Button intent="primary" size="md" className="w-full" onClick={() => onSubmit?.()}>
                Submit Application
            </Button>
        </div>
    );
}
